import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Helmet } from "react-helmet";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import Toast from "../components/Toast";
import SectionLoader from "../components/SectionLoader";
import { getDisplayRoleName } from "../utils/roleUtils";

const permissionTypes = [
  { key: "mview", label: "View" },
  { key: "madd", label: "Add" },
  { key: "medit", label: "Edit" },
  { key: "mdelete", label: "Delete" }
];

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';

  useEffect(() => {
    const fetchRolesAndPermissions = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}roles-permissions`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        setRoles(Array.isArray(data.roles) ? data.roles : []);
        setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
      } catch (err) {
        console.error('Error loading roles & permissions:', err);
        setToast({ open: true, message: 'Failed to load roles and permissions', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchRolesAndPermissions();
  }, [API_URL]);

  const handleRoleChange = async (e) => {
    const roleId = e.target.value;
    setSelectedRole(roleId);
    if (!roleId) {
      setRolePermissions({});
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}user-permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: roleId })
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      let formatted = {};
      if (data.data && data.data.length > 0) {
        data.data.forEach((perm) => {
          formatted[perm.module_id] = {
            mview: perm.mview === "yes",
            madd: perm.madd === "yes",
            medit: perm.medit === "yes",
            mdelete: perm.mdelete === "yes",
            module: perm.module
          };
        });
      } else {
        const defaultPerms = {};
        permissions.forEach((perm) => {
          defaultPerms[perm.id] = {
            mview: false,
            madd: false,
            medit: false,
            mdelete: false,
            module: perm.module
          };
        });
        formatted = defaultPerms;
      }
      setRolePermissions(formatted);
    } catch (err) {
      setToast({ open: true, message: 'Failed to load permissions for role', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (permissionId, type, module) => (event) => {
    setRolePermissions(prev => ({
      ...prev,
      [permissionId]: {
        ...prev[permissionId],
        [type]: event.target.checked,
        module: module
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedRole) {
      setToast({ open: true, message: 'Please select a role first', severity: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const formattedPermissions = Object.keys(rolePermissions).map(permissionId => ({
        role_id: selectedRole,
        permission_id: parseInt(permissionId),
        mview: rolePermissions[permissionId]?.mview ? "yes" : "no",
        madd: rolePermissions[permissionId]?.madd ? "yes" : "no",
        medit: rolePermissions[permissionId]?.medit ? "yes" : "no",
        mdelete: rolePermissions[permissionId]?.mdelete ? "yes" : "no",
        module: rolePermissions[permissionId]?.module
      }));

      const response = await fetch(`${API_URL}save-role-permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role_id: selectedRole, permissions: formattedPermissions })
      });

      const result = await response.json();

      if (!response.ok || result.success === false) {
        if (result.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        setToast({ open: true, message: result.message || 'Failed to save permissions', severity: 'error' });
      } else {
        setToast({ open: true, message: 'Permissions updated successfully!', severity: 'success' });
      }
    } catch (err) {
      setToast({ open: true, message: 'Network error saving permissions', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Helmet>
        <title>Roles & Permissions - GPN</title>
      </Helmet>

      {/* Header Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)",
          borderRadius: 3,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: "white",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: "rgba(76, 161, 175, 0.2)",
              border: "1px solid rgba(76, 161, 175, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4CA1AF",
            }}
          >
            <SecurityIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
              Roles & Permissions Matrix
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              Granular access control matrix for View, Add, Edit, and Delete actions across all modules.
            </Typography>
          </Box>
        </Box>

        {selectedRole && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #4CA1AF 0%, #177373 100%)",
              color: "#ffffff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: "0 4px 14px rgba(76, 161, 175, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #3d8b99 0%, #115858 100%)",
                boxShadow: "0 6px 20px rgba(76, 161, 175, 0.5)",
              },
            }}
          >
            {saving ? "Saving..." : "Save Matrix"}
          </Button>
        )}
      </Box>

      <Toast toast={toast} setToast={setToast} />

      {/* Main Content Paper */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel sx={{ color: "rgba(255, 255, 255, 0.7)" }}>Select Target Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Select Target Role"
              data-testid="role-select"
              sx={{
                borderRadius: 2,
                color: "#FFFFFF",
                background: "rgba(255, 255, 255, 0.05)",
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                "&:hover fieldset": { borderColor: "#4CA1AF" },
                "&.Mui-focused fieldset": { borderColor: "#4CA1AF" },
              }}
            >
              <MenuItem value="">
                <em>-- Select Role --</em>
              </MenuItem>
              {roles
                .filter(role => role.id !== "1" && role.id !== "4")
                .map(role => (
                  <MenuItem key={role.id} value={role.id}>{getDisplayRoleName(role)}</MenuItem>
                ))
              }
            </Select>
          </FormControl>

          {selectedRole && (
            <Chip
              label="Editing Active Permissions"
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {loading ? (
          <SectionLoader />
        ) : !selectedRole ? (
          <Box sx={{ textAlign: "center", py: 6, color: "rgba(255, 255, 255, 0.6)" }}>
            <SecurityIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body1">
              Please select a role from the dropdown above to view and configure its permissions matrix.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: "rgba(76, 161, 175, 0.12)" }}>
                  <TableCell sx={{ color: "#4CA1AF", fontWeight: 700 }}>Module Name</TableCell>
                  {permissionTypes.map(type => (
                    <TableCell key={type.key} align="center" sx={{ color: "#4CA1AF", fontWeight: 700, width: 110 }}>
                      {type.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map(perm => (
                  <TableRow
                    key={perm.id}
                    sx={{
                      "&:hover": { background: "rgba(255, 255, 255, 0.04)" },
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    <TableCell sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                      {perm.module}
                    </TableCell>
                    {permissionTypes.map(type => (
                      <TableCell align="center" key={type.key}>
                        <Checkbox
                          checked={!!rolePermissions[perm.id]?.[type.key]}
                          onChange={handleCheckboxChange(perm.id, type.key, perm.module)}
                          sx={{
                            color: "rgba(76, 161, 175, 0.5)",
                            "&.Mui-checked": {
                              color: "#4CA1AF",
                            },
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default RolesPermissions;